import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  useTheme,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AutoCompleteSupervisor from '../../../../components/input/AutoCompleteSupervisor';
import { FormLabel } from '../../../../components/form/parts';
import RegularInput from '../../../../components/input/RegularInput';
import CustomRadio from '../../../../components/form/CustomRadio';
import { secondaryColors } from '../../../../theme/themeConfig';
import Banner from '../../../../components/shared/Banner';
import Filter from '../../../../components/list/Filter';
import useCompanyStore from '../../../../store/zustand/company';

interface Props {
  saveStepper: ({ next }: any) => void;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  setValues: React.Dispatch<React.SetStateAction<any>>;
  values: any;
}
export enum SupervisorFrequencyEnum {
  ANNUALLY = 'Annually',
  BIANNUALLY = 'Biannually',
  BIWEEKLY = 'Biweekly',
  FULL_TIME = 'FullTime',
  MONTHLY = 'Monthly',
  QUARTERLY = 'Quarterly',
  WEEKLY = 'Weekly'
}

// Number of periods in a year — used to convert annual supervisor hours to
// the per-period value the user enters in "How many hours?". FullTime is
// excluded from auto-suggest (a full-time supervisor isn't ratio-derived).
const PERIODS_PER_YEAR: Record<string, number> = {
  [SupervisorFrequencyEnum.ANNUALLY]: 1,
  [SupervisorFrequencyEnum.BIANNUALLY]: 2,
  [SupervisorFrequencyEnum.QUARTERLY]: 4,
  [SupervisorFrequencyEnum.MONTHLY]: 12,
  [SupervisorFrequencyEnum.BIWEEKLY]: 26,
  [SupervisorFrequencyEnum.WEEKLY]: 52
};

function sumServiceTimesAnnualHours(serviceTimes: any[]): number {
  return (serviceTimes ?? []).reduce((acc: number, st: any) => {
    const weekly = Number(
      st?.availability?.generalInfo?.serviceTimeHoursPerWeek ??
        st?.serviceTimeHoursPerWeek ??
        0
    );
    const weeks = Number(
      st?.availability?.generalInfo?.operatingWeeks ??
        st?.operatingWeeks ??
        52
    );
    return acc + weekly * weeks;
  }, 0);
}

function laborHoursPerYearFromLead(values: any): number {
  // 1) Prefer the pricing snapshot when the workloading step has run.
  const fromPricing = Number(
    values?.pricing?.totalHours ??
      values?.pricing?.totalHoursPerYear ??
      values?.pricing?.hoursPerYear ??
      0
  );
  if (fromPricing > 0) return fromPricing;

  // 2) Newer shape — service times are nested under destinations on the lead.
  const fromDestinations = (values?.destinations ?? []).reduce(
    (acc: number, dest: any) =>
      acc + sumServiceTimesAnnualHours(dest?.serviceTimes ?? []),
    0
  );
  if (fromDestinations > 0) return fromDestinations;

  // 3) Older / flattened shape used elsewhere (e.g. Analysis step).
  return sumServiceTimesAnnualHours(values?.serviceTimes ?? []);
}

const AssignSupervisor: React.FC<Props> = function AssignSupervisor({
  saveStepper,
  setValues,
  setStep,
  values
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  // Tenant ratio = "tech hours per supervisor hour". 0 means supervision is
  // disabled at the company level — no suggestion.
  const ratio = Number(
    useCompanyStore((s) => s.company?.maxHoursWithoutSupervision) ?? 0
  );

  const [enableSupervisor, setEnableSupervisor] = useState<
    'enabled' | 'disabled'
  >(() => {
    // Existing lead has explicit choice — honor it.
    if (values?.supervisorEnabled === true || values?.supervisor) {
      return 'enabled';
    }
    if (values?.supervisorEnabled === false) {
      return 'disabled';
    }
    // New lead: default based on whether the company expects supervision.
    // Ratio > 0 means the tenant has set "1 supervisor hr per N tech hrs";
    // ratio = 0 means "Disable supervisor hours" was checked.
    return ratio > 0 ? 'enabled' : 'disabled';
  });

  const [showWarning, setShowWarning] = useState(false);
  const Frequencies = [
    SupervisorFrequencyEnum.WEEKLY,
    SupervisorFrequencyEnum.BIWEEKLY,
    SupervisorFrequencyEnum.MONTHLY,
    SupervisorFrequencyEnum.QUARTERLY,
    SupervisorFrequencyEnum.BIANNUALLY,
    SupervisorFrequencyEnum.ANNUALLY,
    SupervisorFrequencyEnum.FULL_TIME
  ];
  const laborHoursPerYear = laborHoursPerYearFromLead(values);
  const periodsPerYear = values?.supervisorFrequency
    ? PERIODS_PER_YEAR[values.supervisorFrequency]
    : undefined;

  const suggestedHoursPerPeriod = useMemo(() => {
    if (!ratio || ratio <= 0) return null;
    if (!laborHoursPerYear || laborHoursPerYear <= 0) return null;
    if (!periodsPerYear) return null;
    const annualSupervisorHours = laborHoursPerYear / ratio;
    const perPeriod = annualSupervisorHours / periodsPerYear;
    // Round to one decimal — matches the precision of the input.
    return Math.round(perPeriod * 10) / 10;
  }, [ratio, laborHoursPerYear, periodsPerYear]);

  // When supervisor is enabled (either by user toggle or by ratio-based
  // default on mount), make sure supervisorEnabled and a default frequency
  // are set in values — otherwise the suggestion below can't compute
  // periodsPerYear and the parent won't see the enabled state.
  useEffect(() => {
    if (enableSupervisor !== 'enabled') return;
    const patch: Record<string, unknown> = {};
    if (values?.supervisorEnabled !== true) patch.supervisorEnabled = true;
    if (!values?.supervisorFrequency) {
      patch.supervisorFrequency = SupervisorFrequencyEnum.WEEKLY;
    }
    if (Object.keys(patch).length > 0) {
      setValues({ ...values, ...patch });
    }
  }, [enableSupervisor]);

  // Prefill supervisorTime when a suggestion becomes available and the user
  // hasn't typed anything yet. Never overwrites a non-zero entry.
  useEffect(() => {
    if (enableSupervisor !== 'enabled') return;
    if (suggestedHoursPerPeriod == null) return;
    const current = Number(values?.supervisorTime ?? 0);
    if (current > 0) return;
    setValues({ ...values, supervisorTime: suggestedHoursPerPeriod });
  }, [suggestedHoursPerPeriod, enableSupervisor]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const newValues = { ...values };

    if (value === 'enabled') {
      setEnableSupervisor('enabled');
    } else {
      delete newValues.supervisor;
      delete newValues.supervisorTime;
      delete newValues.supervisorFrequency;
      setEnableSupervisor('disabled');
    }
    setValues({
      ...newValues,
      supervisorEnabled: value === 'enabled'
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="32px"
      key="assign-supervisor-div-main"
    >
      <Box
        display="flex"
        flexDirection="column"
        gap={4}
        justifyContent="space-between"
      >
        <RadioGroup onChange={handleChange} value={enableSupervisor}>
          <Box display="flex" flexDirection="row" gap="24px">
            <FormControlLabel
              control={<CustomRadio />}
              disabled={values?.readOnly}
              label={
                <Typography
                  color={secondaryColors.SECONDARY_3}
                  variant="body_300"
                >
                  {t('LEAD.CREATE.SERVICES.SCHEDULE.assingSupervisor')}
                </Typography>
              }
              sx={{ margin: 0 }}
              value="enabled"
            />
            <FormControlLabel
              control={<CustomRadio />}
              disabled={values?.readOnly}
              label={
                <Typography
                  color={secondaryColors.SECONDARY_3}
                  variant="body_300"
                >
                  {t('LEAD.CREATE.SERVICES.SCHEDULE.dontAssingSupervisor')}
                </Typography>
              }
              sx={{ margin: 0 }}
              value="disabled"
            />
          </Box>
        </RadioGroup>

        {enableSupervisor === 'enabled' && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
            <Box display="flex" flex={1} flexDirection="column" gap="4px">
              <Typography color="primary.main" variant="heading_300">
                {t(
                  'LEAD.CREATE.SERVICES.SCHEDULE.assingSupervisorAutocomplete'
                )}
              </Typography>
              <AutoCompleteSupervisor
                disabled={values?.readOnly}
                multiple={false}
                setValues={(supervisor) => {
                  setValues({ ...values, supervisor });
                }}
                type="supervisor"
                values={values || []}
              />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              gap="4px"
              sx={{
                minWidth: '318px'
              }}
            >
              <FormLabel
                label={t('LEAD.CREATE.SERVICES.SCHEDULE.assingSupervisorHours')}
              />
              <RegularInput
                customStyle={{
                  '& .MuiInputBase-input': { height: '40px' },
                  margin: 0
                }}
                disabled={values?.readOnly}
                onChange={(supervisorTime) => {
                  setValues({ ...values, supervisorTime });
                }}
                type="number"
                value={values?.supervisorTime}
              />
              {suggestedHoursPerPeriod != null && (
                <Typography
                  color={secondaryColors.SECONDARY_3}
                  sx={{ fontSize: '12px', mt: 0.5 }}
                  variant="body_200"
                >
                  Suggested: {suggestedHoursPerPeriod} hr (
                  {laborHoursPerYear.toFixed(0)} labor hrs/yr ÷ {ratio}:1 ratio)
                </Typography>
              )}
            </Box>
            <Box display="flex" flexDirection="column" gap={0.5}>
              <FormLabel label={t('LEAD.CREATE.SUPERVISION.frecuency')} />
              <Filter
                arrayInput={Frequencies.map((frequency: string) =>
                  t(`WIDGETS.OPTIONS.INTERVAL.${frequency}`)
                )}
                backgroundColor={theme.palette.background.default}
                customStyle={{ height: 56, margin: 0 }}
                disabled={values?.readOnly}
                selectInput={
                  values?.supervisorFrequency
                    ? t(
                        `WIDGETS.OPTIONS.INTERVAL.${values?.supervisorFrequency}`
                      )
                    : ''
                }
                setSelectInput={(supervisorFrequency: any) => {
                  setValues({
                    ...values,
                    supervisorFrequency: supervisorFrequency.replace(/\s+/g, '')
                  });
                }}
                title="Frequency"
              />
            </Box>
          </Box>
        )}
      </Box>
      {showWarning && (
        <Banner
          icon
          text={t('LEAD.CREATE.SERVICES.SCHEDULE.assingSupervisorRequired')}
          type="error"
        />
      )}
    </Box>
  );
};

export default AssignSupervisor;
